import React, { useEffect, useState } from 'react';
import { fetchRecommendedProductsForOrder, logEvent, saveTestSelections } from '../util/apiUtils';
import AddAnotherTest, { canAddAnotherTest } from '../confirm/AddAnotherTest';
import AddToOrderModal from './AddToOrderModal';
import { isGovernmentPayorOrder } from '../util/gearOrderUtil';
import ProductCard from './ProductCard';
import Footer from './Footer';

// Maximum number of recommended product cards to fetch
const MAXIMUM_RECOMMENDED_PRODUCTS = 3;

/**
 * Displays a Recommended Products section, or the default Add Another Test button if no recommendations are found.
 */
export default function RecommendedProducts({
	gearOrder,
	userInfo,
	addNewRequisition,
	omsRequisition,
	loadSavedRecommendedProduct
}) {
	const [recommendedProducts, setRecommendedProducts] = useState([]);
	const [canAddAnother, setCanAddAnother] = useState(false);
	const [productToAdd, setProductToAdd] = useState(null);
	const [loading, setLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState(null);

	/**
	 * Call the canAddAnotherTest method to determine if the order is in a state that is
	 * appropriate for adding another test.
	 */
	const hasAddNewRequisitionMethod = !!addNewRequisition;
	useEffect(() => {
		setCanAddAnother(canAddAnotherTest(gearOrder, hasAddNewRequisitionMethod));
	}, [gearOrder, hasAddNewRequisitionMethod]);

	/**
	 * Fetch a new list of recommended products any time the list of orderable items changes.
	 *
	 * Using the orderableItemIds array as a useEffect dependency is causing the effect to trigger on each render.
	 * To get around this I am converting the array to a comma-separated string for the dependency check.
	 */
	const orderableItemIds = gearOrder?.requisitions?.map((gearReq) => gearReq.omsOrderableItemId);
	const orderableItemIdsString = orderableItemIds?.join(',');
	const gearOrderId = gearOrder?.id;
	const isClient = userInfo?.type === 'myGDX';
	const isMedicareOrder = isGovernmentPayorOrder(gearOrder);
	useEffect(() => {
		setProductToAdd(null);

		if (canAddAnother && isClient && !isMedicareOrder) {
			fetchRecommendedProductsForOrder(gearOrderId, MAXIMUM_RECOMMENDED_PRODUCTS)
				.then((recommendedProducts) => {
					setRecommendedProducts(recommendedProducts);
				})
				.catch((err) => {
					// Clear the recommened products list to hide this component on error
					setRecommendedProducts([]);
				});
		} else {
			setRecommendedProducts([]);
		}
	}, [canAddAnother, gearOrderId, isClient, isMedicareOrder, orderableItemIdsString]);

	/**
	 * Handle a request to close the Add to Order modal.
	 * If the order was updated with a new requisition we'll receive an updateOrderRequest with the new gearOrder and
	 * omsRequisition. Pass those to the parent.
	 * Otherwise, set productToAdd to null to hide the modal and reset state.
	 */
	const onCloseModal = (updateOrderRequest) => {
		if (updateOrderRequest) {
			loadSavedRecommendedProduct(updateOrderRequest);
		} else {
			setProductToAdd(null);
		}
	};

	/**
	 * Check the order type when "Add to Order" is clicked.
	 * ShipToOffice orders: Save the new product to the order (with quantity=1).
	 * Other orders: Log the click and set productToAdd (to open the AddToOrderModal).
	 */
	const handleAddToOrderClick = (recommendedProduct) => {
		if (gearOrder?.containsShipToOfficeRequisition) {
			handleBulkOrderAdd(recommendedProduct);
		} else {
			logEvent(
				'{} - Recommended Products "Add to Order" clicked. gearOrderId={}, recommendedProduct={}',
				'RECOMMENDED_PRODUCTS_ADD_TO_ORDER_CLICK',
				gearOrder.id,
				recommendedProduct.omsOrderableItemId
			);

			setProductToAdd(recommendedProduct);
		}
	};

	/**
	 * Save a new requisition on the order with quantity=1 and push the new order to the parent.
	 * The new product well will display on the confirmation page where the user can update quantity
	 *  or test customizations.
	 */
	const handleBulkOrderAdd = (recommendedProduct) => {
		let gearOrderToSubmit = Object.assign({}, gearOrder);

		// Save the new product with quantity = 1. The user will be able to adjust the quantity and test customization after it is saved.
		let gearReq = {
			omsOrderableItemId: recommendedProduct.omsOrderableItemId,
			components: [],
			addons: [],
			shipToOffice: true,
			quantity: 1
		};

		// Ship to Office must include previous + new requisitions
		gearOrderToSubmit.requisitions = [...gearOrder.requisitions, gearReq];

		setErrorMessage(null);
		setLoading(true);
		saveTestSelections(gearOrderToSubmit)
			.then((json) => {
				// Find the new gearReqId so we can log it
				let newGearReq = json?.requisitions?.find(
					(gearReq) => gearReq.omsOrderableItemId === recommendedProduct.omsOrderableItemId
				);
				logEvent(
					`{} - Recommended Product saved to order. gearOrderId={}, gearReqId={}, omsOrderableItemId={}`,
					'RECOMMENDED_PRODUCTS_BULK_SAVE',
					gearOrder.id,
					newGearReq?.id,
					recommendedProduct.omsOrderableItemId
				);

				// Pass the updated order and new gearReqId to the parent
				loadSavedRecommendedProduct(json, newGearReq?.id);
			})
			.catch((error) => {
				setErrorMessage(
					<div className="alert alert-danger">
						<span>
							An unexpected error occurred. Please try again or call us at 800-522-4762 or contact{' '}
							<a
								href={`${process.env.REACT_APP_GDX_WEB_URL}/contact/providers#contact-us`}
								className="alert-link"
								target="_blank"
								rel="noopener noreferrer"
							>
								Customer Support
							</a>{' '}
							for help.
						</span>
					</div>
				);
			})
			.finally(() => {
				setLoading(false);
			});
	};

	if (!canAddAnother) {
		return null;
	} else if (!recommendedProducts?.length) {
		// Don't show the generic "Add Another Test" button for Bulk orders
		if (gearOrder?.containsShipToOfficeRequisition) {
			return null;
		}
		return <AddAnotherTest gearOrder={gearOrder} addNewRequisition={addNewRequisition} />;
	}

	// Show a drop-ship message if the original order only contains Online Activations
	const showDropShipNotice = !gearOrder?.containsShipToOfficeRequisition && !gearOrder?.containsDropShipRequisition;

	return (
		<>
			<div className="panel panel-default" style={{ position: 'relative' }}>
				<div className="panel-body">
					<h3 className="mt-0">
						<strong>Products related to this order</strong>
						{showDropShipNotice && (
							<>
								{' '}
								<br className="visible-xs-block" />
								<small>Add a collection pack to be shipped to your patient</small>
							</>
						)}
					</h3>
					<div className="row display-flex">
						{recommendedProducts?.map((product) => (
							<ProductCard
								key={product.omsOrderableItemId}
								recommendedProduct={product}
								addToOrder={handleAddToOrderClick}
							/>
						))}
					</div>
					{errorMessage}
					<Footer addNewRequisition={addNewRequisition} />
				</div>
				{loading && (
					<div className="loading-shield" style={{ position: 'absolute' }}>
						<i
							className="fa fa-spinner fa-spin fa-fw loading-icon"
							style={{ top: '30%', position: 'absolute', width: '50%', left: '25%' }}
							aria-hidden="true"
						></i>
					</div>
				)}
			</div>
			{!!productToAdd && (
				<AddToOrderModal
					recommendedProduct={productToAdd}
					onCloseModal={onCloseModal}
					userInfo={userInfo}
					gearOrder={gearOrder}
				/>
			)}
		</>
	);
}
